import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "./useAuth";
import { roleSlug } from "./roleMap";
import type { Role } from "@ub/shared";

/**
 * Multi-role switching primitives. Pure — no side effects — so it is safe to
 * call from many components (RoleMenu, each header). The horizontal-swipe
 * gesture is attached to the header row (MarqueeHeader), so it only fires on a
 * swipe over the role bar — not over the scrollable content below.
 */
export function useRoleSwitch() {
  const { availableRoles, activeRole, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const canSwitch = availableRoles.length > 1;

  const switchTo = useCallback(
    (role: Role) => {
      if (role === activeRole) return;
      setActiveRole(role);
      navigate(`/${roleSlug(role)}`, { replace: true });
    },
    [activeRole, setActiveRole, navigate],
  );

  const cycle = useCallback(
    (dir: 1 | -1) => {
      if (availableRoles.length < 2) return;
      const cur = activeRole ?? availableRoles[0]!;
      const idx = availableRoles.indexOf(cur);
      const next = availableRoles[(idx + dir + availableRoles.length) % availableRoles.length];
      if (next) switchTo(next);
    },
    [availableRoles, activeRole, switchTo],
  );

  return { roles: availableRoles, activeRole, canSwitch, switchTo, cycle };
}
